/**
 * i18n — FR-default with full AR/RTL. Deliberately dependency-free (ENGINEERING_STANDARDS: no
 * unnecessary heavy deps). Flat dot-keyed dictionaries per locale; features register their own
 * namespace via registerMessages() so no central file owns every string. Missing key -> the FR
 * value -> the raw key (never a crash, never a blank).
 *
 *   const { t, locale } = useT();
 *   t("nav.dashboard")                          // "Tableau de bord"
 *   t("reception.dossier.count", { n: 3 })      // interpolation: "{n} dossiers"
 */
/* eslint-disable react-refresh/only-export-components -- provider + useT hook + registerMessages co-locate by design. */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useUiStore, type Locale } from "./uiStore";

export type Messages = Record<string, string>;

const dictionaries: Record<Locale, Messages> = {
  fr: {
    "app.name": "Novanalyse",
    "app.signature": "Écosystème Pharmakon",
    "nav.dashboard": "Tableau de bord",
    "action.search": "Rechercher",
    "action.retry": "Réessayer",
    "action.close": "Fermer",
    "action.logout": "Se déconnecter",
    "action.skipToContent": "Aller au contenu principal",
    "region.main": "Contenu principal",
    "nav.toggle": "Basculer la navigation",
    "nav.primary": "Navigation principale",
    "locale.toggle": "Changer de langue",
    "density.toggle": "Basculer la densité d'affichage",
    "theme.group": "Thème",
    "theme.pro": "Pro",
    "theme.claire": "Claire",
    "theme.dark": "Nuit",
    "auth.login.title": "Connexion",
    "auth.login.subtitle": "Accédez à votre laboratoire numérique.",
    "auth.login.email": "Adresse e-mail",
    "auth.login.email.placeholder": "vous@laboratoire.dz",
    "auth.login.labref": "Code laboratoire (facultatif)",
    "auth.login.labref.placeholder": "Ex. LAB-0001",
    "auth.login.labref.hint": "Laissez ce champ vide sauf si votre compte gère plusieurs laboratoires — indiquez alors le code du laboratoire à ouvrir.",
    "auth.login.labref.toggle.show": "J'accède à plusieurs laboratoires",
    "auth.login.password": "Mot de passe",
    "auth.login.password.placeholder": "Votre mot de passe",
    "auth.login.submit": "Se connecter",
    "auth.login.submitting": "Connexion…",
    "auth.login.error": "Identifiants invalides.",
    // Shown under a failed login: reminds a multi-lab user that the lab code may be required. Kept
    // enumeration-safe — it never confirms whether the email/lab exists, only guides the multi-lab case.
    "auth.login.error.multiLabHint":
      "Vous gérez plusieurs laboratoires ? Vérifiez le code du laboratoire ci-dessous, puis réessayez.",
    "auth.login.remember": "Se souvenir de moi",
    "auth.login.forgot": "Mot de passe oublié ?",
    "auth.login.pw.show": "Afficher le mot de passe",
    "auth.login.pw.hide": "Masquer le mot de passe",
    "auth.login.captcha.label": "Je ne suis pas un robot",
    "auth.login.captcha.verifying": "Vérification…",
    "auth.login.captcha.verified": "Vérifié",
    "auth.login.captcha.error": "La vérification a échoué.",
    "auth.login.captcha.retry": "Réessayer",
    "auth.login.secure": "Connexion chiffrée · Conforme Loi 18-07",
    "auth.signup.prompt": "Nouveau laboratoire ?",
    "auth.signup.cta": "Demander un accès",
    "auth.signup.note": "validé par Pharmakon sous 48 h",

    "auth.forgot.title": "Mot de passe oublié",
    "auth.forgot.subtitle":
      "Saisissez votre adresse e-mail ; si un compte existe, un lien de réinitialisation vous sera envoyé.",
    "auth.forgot.email": "Adresse e-mail",
    "auth.forgot.submit": "Envoyer le lien",
    "auth.forgot.submitting": "Envoi…",
    "auth.forgot.done.title": "Vérifiez votre boîte de réception",
    "auth.forgot.done":
      "Si un compte correspond à cette adresse, un e-mail contenant un lien de réinitialisation vient d'être envoyé. Le lien expire dans 60 minutes.",
    "auth.forgot.error": "Demande impossible pour le moment — réessayez plus tard.",
    "auth.forgot.ratelimited": "Trop de tentatives. Patientez quelques minutes avant de réessayer.",
    "auth.forgot.back": "Retour à la connexion",

    "auth.reset.title": "Nouveau mot de passe",
    "auth.reset.subtitle": "Choisissez un nouveau mot de passe pour votre compte.",
    "auth.reset.password": "Nouveau mot de passe",
    "auth.reset.password.placeholder": "Au moins 8 caractères",
    "auth.reset.confirm": "Confirmer le mot de passe",
    "auth.reset.submit": "Réinitialiser le mot de passe",
    "auth.reset.submitting": "Réinitialisation…",
    "auth.reset.done.title": "Mot de passe mis à jour",
    "auth.reset.done": "Votre mot de passe a été réinitialisé. Vous pouvez maintenant vous connecter.",
    "auth.reset.error": "La réinitialisation a échoué — le lien est peut-être expiré ou invalide.",
    "auth.reset.ratelimited": "Trop de tentatives. Patientez quelques minutes avant de réessayer.",
    "auth.reset.invalidlink.title": "Lien invalide",
    "auth.reset.invalidlink":
      "Ce lien de réinitialisation est invalide ou a expiré. Demandez-en un nouveau.",
    "auth.reset.requestnew": "Demander un nouveau lien",
    "auth.reset.tologin": "Aller à la connexion",
    "auth.reset.rule.length": "Au moins {n} caractères.",
    "auth.reset.rule.classes": "Au moins {n} types de caractères (lettres, chiffres, symboles).",
    "auth.reset.rule.match": "Les deux mots de passe doivent être identiques.",

    "auth.demande.title": "Demander un accès",
    "auth.demande.subtitle":
      "Nouveau laboratoire ? Votre demande est validée par Pharmakon sous 48 h.",
    "auth.demande.lab": "Nom du laboratoire",
    "auth.demande.lab.placeholder": "Laboratoire Central d'Analyses",
    "auth.demande.owner": "Nom du responsable",
    "auth.demande.owner.placeholder": "Dr. Prénom Nom",
    "auth.demande.email": "E-mail professionnel",
    "auth.demande.email.placeholder": "responsable@laboratoire.dz",
    "auth.demande.phone": "Téléphone",
    "auth.demande.phone.placeholder": "0XX XX XX XX",
    "auth.demande.wilaya": "Wilaya",
    "auth.demande.wilaya.placeholder": "Choisir une wilaya",
    "auth.demande.commune": "Commune",
    "auth.demande.commune.placeholder": "Commune du laboratoire",
    "auth.demande.message": "Message (facultatif)",
    "auth.demande.message.placeholder": "Précisez votre activité, vos automates, vos volumes…",
    "auth.demande.submit": "Envoyer la demande",
    "auth.demande.submitting": "Envoi…",
    "auth.demande.done.title": "Demande reçue",
    "auth.demande.done":
      "Votre demande a bien été enregistrée. L'équipe Pharmakon l'examine et vous contacte sous 48 h ouvrées à l'adresse indiquée.",
    "auth.demande.pending.title": "Demande en attente d'enregistrement",
    "auth.demande.pending":
      "Le service d'inscription en ligne n'est pas encore actif. Votre demande a été conservée : contactez-nous à inscriptions@novera-dz.com en mentionnant votre laboratoire, ou réessayez plus tard.",
    "auth.demande.error": "L'envoi a échoué — vérifiez votre connexion et réessayez.",
    "auth.demande.ratelimited": "Trop de demandes. Patientez quelques minutes avant de réessayer.",
    "auth.demande.back": "Retour à la connexion",
    "auth.demande.required": "Ce champ est requis.",
    "auth.demande.email.invalid": "Adresse e-mail invalide.",
    "auth.demande.captcha.verifying": "Vérification anti-robot en cours…",
    "auth.demande.captcha.failed": "La vérification anti-robot a échoué — réessayez.",

    "density.confort": "Confort",
    "density.compact": "Compact",
    "locale.fr": "Français",
    "locale.ar": "العربية",
    "state.offline.title": "Hors ligne",
    "state.forbidden.title": "Accès refusé",
    "error.boundary.title": "Une erreur est survenue",
    "error.boundary.message": "L'écran a rencontré un problème inattendu.",
  },
  ar: {
    "app.name": "نوفاناليز",
    "app.signature": "منظومة فارماكون",
    "nav.dashboard": "لوحة القيادة",
    "action.search": "بحث",
    "action.retry": "إعادة المحاولة",
    "action.close": "إغلاق",
    "action.logout": "تسجيل الخروج",
    "action.skipToContent": "الانتقال إلى المحتوى الرئيسي",
    "region.main": "المحتوى الرئيسي",
    "nav.toggle": "تبديل التنقل",
    "nav.primary": "التنقل الرئيسي",
    "locale.toggle": "تغيير اللغة",
    "density.toggle": "تبديل كثافة العرض",
    "theme.group": "السمة",
    "theme.pro": "احترافي",
    "theme.claire": "فاتح",
    "theme.dark": "ليلي",
    "auth.login.title": "تسجيل الدخول",
    "auth.login.subtitle": "ادخل إلى مختبرك الرقمي.",
    "auth.login.email": "البريد الإلكتروني",
    "auth.login.email.placeholder": "you@laboratoire.dz",
    "auth.login.labref": "رمز المختبر (اختياري)",
    "auth.login.labref.placeholder": "مثال: LAB-0001",
    "auth.login.labref.hint": "اترك هذا الحقل فارغًا إلا إذا كان حسابك يدير عدة مختبرات — عندئذٍ أدخل رمز المختبر المراد فتحه.",
    "auth.login.labref.toggle.show": "أصل إلى عدة مختبرات",
    "auth.login.password": "كلمة المرور",
    "auth.login.password.placeholder": "كلمة المرور الخاصة بك",
    "auth.login.submit": "دخول",
    "auth.login.submitting": "جارٍ تسجيل الدخول…",
    "auth.login.error": "بيانات الاعتماد غير صحيحة.",
    "auth.login.error.multiLabHint":
      "هل تدير عدة مختبرات؟ تحقّق من رمز المختبر أدناه ثم أعد المحاولة.",
    "auth.login.remember": "تذكّرني",
    "auth.login.forgot": "نسيت كلمة المرور؟",
    "auth.login.pw.show": "إظهار كلمة المرور",
    "auth.login.pw.hide": "إخفاء كلمة المرور",
    "auth.login.captcha.label": "لست روبوتًا",
    "auth.login.captcha.verifying": "جارٍ التحقق…",
    "auth.login.captcha.verified": "تم التحقق",
    "auth.login.captcha.error": "فشل التحقق.",
    "auth.login.captcha.retry": "إعادة المحاولة",
    "auth.login.secure": "اتصال مشفّر · متوافق مع القانون 18-07",
    "auth.signup.prompt": "مختبر جديد؟",
    "auth.signup.cta": "اطلب وصولاً",
    "auth.signup.note": "تتم الموافقة من قِبل Pharmakon خلال 48 ساعة",

    "auth.forgot.title": "نسيت كلمة المرور",
    "auth.forgot.subtitle":
      "أدخل بريدك الإلكتروني؛ إذا كان هناك حساب، فسيُرسل إليك رابط لإعادة التعيين.",
    "auth.forgot.email": "البريد الإلكتروني",
    "auth.forgot.submit": "إرسال الرابط",
    "auth.forgot.submitting": "جارٍ الإرسال…",
    "auth.forgot.done.title": "تحقّق من بريدك الوارد",
    "auth.forgot.done":
      "إذا كان هناك حساب مطابق لهذا العنوان، فقد أُرسل بريد إلكتروني يحتوي على رابط لإعادة التعيين. تنتهي صلاحية الرابط خلال 60 دقيقة.",
    "auth.forgot.error": "تعذّر تنفيذ الطلب حاليًا — أعد المحاولة لاحقًا.",
    "auth.forgot.ratelimited": "محاولات كثيرة. انتظر بضع دقائق قبل إعادة المحاولة.",
    "auth.forgot.back": "العودة إلى تسجيل الدخول",

    "auth.reset.title": "كلمة مرور جديدة",
    "auth.reset.subtitle": "اختر كلمة مرور جديدة لحسابك.",
    "auth.reset.password": "كلمة المرور الجديدة",
    "auth.reset.password.placeholder": "8 أحرف على الأقل",
    "auth.reset.confirm": "تأكيد كلمة المرور",
    "auth.reset.submit": "إعادة تعيين كلمة المرور",
    "auth.reset.submitting": "جارٍ إعادة التعيين…",
    "auth.reset.done.title": "تم تحديث كلمة المرور",
    "auth.reset.done": "تمت إعادة تعيين كلمة المرور. يمكنك الآن تسجيل الدخول.",
    "auth.reset.error": "فشلت إعادة التعيين — قد يكون الرابط منتهي الصلاحية أو غير صالح.",
    "auth.reset.ratelimited": "محاولات كثيرة. انتظر بضع دقائق قبل إعادة المحاولة.",
    "auth.reset.invalidlink.title": "رابط غير صالح",
    "auth.reset.invalidlink": "رابط إعادة التعيين هذا غير صالح أو منتهي الصلاحية. اطلب رابطًا جديدًا.",
    "auth.reset.requestnew": "طلب رابط جديد",
    "auth.reset.tologin": "الذهاب إلى تسجيل الدخول",
    "auth.reset.rule.length": "{n} أحرف على الأقل.",
    "auth.reset.rule.classes": "نوعان على الأقل من المحارف (حروف، أرقام، رموز): {n}.",
    "auth.reset.rule.match": "يجب أن تتطابق كلمتا المرور.",

    "auth.demande.title": "طلب وصول",
    "auth.demande.subtitle": "مختبر جديد؟ تتم الموافقة على طلبك من قِبل Pharmakon خلال 48 ساعة.",
    "auth.demande.lab": "اسم المختبر",
    "auth.demande.lab.placeholder": "المختبر المركزي للتحاليل",
    "auth.demande.owner": "اسم المسؤول",
    "auth.demande.owner.placeholder": "د. الاسم واللقب",
    "auth.demande.email": "البريد المهني",
    "auth.demande.email.placeholder": "responsable@laboratoire.dz",
    "auth.demande.phone": "الهاتف",
    "auth.demande.phone.placeholder": "0XX XX XX XX",
    "auth.demande.wilaya": "الولاية",
    "auth.demande.wilaya.placeholder": "اختر ولاية",
    "auth.demande.commune": "البلدية",
    "auth.demande.commune.placeholder": "بلدية المختبر",
    "auth.demande.message": "رسالة (اختياري)",
    "auth.demande.message.placeholder": "حدّد نشاطك وأجهزتك وأحجامك…",
    "auth.demande.submit": "إرسال الطلب",
    "auth.demande.submitting": "جارٍ الإرسال…",
    "auth.demande.done.title": "تم استلام الطلب",
    "auth.demande.done":
      "تم تسجيل طلبك بنجاح. يدرسه فريق Pharmakon ويتواصل معك خلال 48 ساعة عمل على العنوان المذكور.",
    "auth.demande.pending.title": "الطلب في انتظار التسجيل",
    "auth.demande.pending":
      "خدمة التسجيل عبر الإنترنت غير مُفعّلة بعد. تم حفظ طلبك: تواصل معنا على inscriptions@novera-dz.com مع ذكر مختبرك، أو أعد المحاولة لاحقًا.",
    "auth.demande.error": "فشل الإرسال — تحقّق من اتصالك وأعد المحاولة.",
    "auth.demande.ratelimited": "طلبات كثيرة. انتظر بضع دقائق قبل إعادة المحاولة.",
    "auth.demande.back": "العودة إلى تسجيل الدخول",
    "auth.demande.required": "هذا الحقل مطلوب.",
    "auth.demande.email.invalid": "بريد إلكتروني غير صالح.",
    "auth.demande.captcha.verifying": "جارٍ التحقق من أنك لست روبوتًا…",
    "auth.demande.captcha.failed": "فشل التحقق من أنك لست روبوتًا — أعد المحاولة.",

    "density.confort": "مريح",
    "density.compact": "مضغوط",
    "locale.fr": "Français",
    "locale.ar": "العربية",
    "state.offline.title": "غير متصل",
    "state.forbidden.title": "تم رفض الوصول",
    "error.boundary.title": "حدث خطأ",
    "error.boundary.message": "واجهت الشاشة مشكلة غير متوقعة.",
  },
};

/** Features call this at module-load to add their namespaced strings to both locales. */
export function registerMessages(locale: Locale, messages: Messages): void {
  Object.assign(dictionaries[locale], messages);
}

export type TFunction = (key: string, vars?: Record<string, string | number>) => string;

function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`));
}

function translate(locale: Locale, key: string, vars?: Record<string, string | number>): string {
  const fromLocale = dictionaries[locale][key];
  const fromFr = dictionaries.fr[key];
  return interpolate(fromLocale ?? fromFr ?? key, vars);
}

interface I18nContextValue {
  locale: Locale;
  t: TFunction;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const locale = useUiStore((s) => s.locale);
  const value = useMemo<I18nContextValue>(
    () => ({ locale, t: (key, vars) => translate(locale, key, vars) }),
    [locale],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useT(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used within <I18nProvider>");
  return ctx;
}
